
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

export const useSupervisorStatus = () => {
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [supervisorName, setSupervisorName] = useState<string | null>(null);
  const [userPerformanceBand, setUserPerformanceBand] = useState<any>(null);
  const [userFullName, setUserFullName] = useState<string>("");
  const { activeYear } = usePerformanceYear();

  useEffect(() => {
    const checkSupervisorStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          console.log('useSupervisorStatus: No session found');
          setIsLoading(false);
          return;
        }

        console.log('useSupervisorStatus: Session user ID:', session.user.id);

        const { data: currentUserData, error: userError } = await supabase
          .from('users')
          .select('id, full_name, supervisor_id, is_unit_head, role')
          .eq('id', session.user.id)
          .single();

        if (userError) {
          console.error('useSupervisorStatus: Error fetching current user data:', userError);
          setIsLoading(false);
          return;
        }

        console.log('useSupervisorStatus: Current user data:', currentUserData);

        if (currentUserData?.full_name) {
          setUserFullName(currentUserData.full_name);
        }

        // Check if user has subordinates (is a supervisor)
        const { data: subordinates, error: subordinatesError } = await supabase
          .from('users')
          .select('id')
          .eq('supervisor_id', session.user.id)
          .limit(1);

        if (subordinatesError) {
          console.error('useSupervisorStatus: Error checking subordinates:', subordinatesError);
        } else {
          const hasSupervisorRole = subordinates && subordinates.length > 0;
          console.log('useSupervisorStatus: Has subordinates:', hasSupervisorRole, 'Count:', subordinates?.length || 0);
          setIsSupervisor(hasSupervisorRole);
        }

        // Get supervisor's name if user has one
        if (currentUserData?.supervisor_id) {
          const { data: supervisorData, error: supervisorError } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', currentUserData.supervisor_id)
            .single();

          if (supervisorError) {
            console.error('useSupervisorStatus: Error fetching supervisor data:', supervisorError);
          } else {
            setSupervisorName(supervisorData?.full_name || null);
          }
        }

        // Fetch user's performance band - filter by activeYear instead of calendar year
        const { data: performanceBandData, error: bandError } = await supabase
          .from('performance_bands')
          .select('*')
          .eq('employee_id', session.user.id)
          .eq('year', activeYear)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (bandError) {
          console.error('useSupervisorStatus: Error fetching performance band:', bandError);
        } else {
          setUserPerformanceBand(performanceBandData);
        }

      } catch (error) {
        console.error('useSupervisorStatus: Error in checkSupervisorStatus:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSupervisorStatus();
  }, [activeYear]);

  return {
    isSupervisor,
    isLoading,
    supervisorName,
    userPerformanceBand,
    setUserPerformanceBand,
    userFullName
  };
};
